import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';

import {apiAction, apiActionImproved} from '../api';
import {config} from '../config/config.ts';
import {ClubType} from '../constants';
import {useJwtAuth} from '../JWTAuthProvider';
import {itemFor, writeChecklist} from '../onboarding/communityChecklist.ts';
import {setAdsUserData} from '../utilities/adsEmailMatching.ts';
import {startGoogleSignIn} from '../utilities/googleAuth.ts';

import shotMatchCentre from '@/assets/docs/match-centre-board.webp';
import shotCreateGame from '@/assets/docs/create-game.webp';
import shotClubPlayers from '@/assets/docs/club-players.webp';
import shotPlayerDashboard from '@/assets/docs/player-dashboard.webp';
import shotDraws from '@/assets/docs/draws-groups.webp';
import shotRegister from '@/assets/docs/event-page-register.webp';
import shotCrm from '@/assets/docs/club-players-crm.webp';
import shotClubPage from '@/assets/docs/club-public-page.webp';
import shotLeaderboard from '@/assets/docs/leaderboard-league-of-players.webp';
import shotSchedule from '@/assets/docs/schedule-generator.webp';
import shotNotify from '@/assets/docs/notify-players.webp';
import shotOverlayFrame from '@/assets/docs/stream-overlay-frame.webp';
import shotStreamCourt from '@/assets/docs/stream-live-court.webp';
import {sportDefinition, sportsForSurface, useSports} from '@/sports/registry.ts';
import {storedSportKey} from '@/utilities/sportKeys';

export const Route = createFileRoute('/quiz-community')({
    component: CommunityQuizPage,
});

/**
 * The COMMUNITY organiser funnel — a separate quiz from /quiz, deliberately.
 *
 * /quiz sells one event: answer five questions and it builds your draw. Someone running a club
 * all season wants the opposite pitch — what this will do for them week after week — so this asks
 * what they need to manage, shows them those features, and signs them up. The two audiences are
 * sorted at the top of /quiz, which sends anyone running a club here and keeps the one-off
 * organisers for its own builder; everyone on this page has already said they run a club.
 *
 * Kept entirely apart from /quiz: its own route, its own component, its own copy. Responses are
 * tagged variant=community so Admin → Quiz Responses can compare the two funnels rather than
 * averaging them together.
 */
export const R_KEY = 'sp_quiz_rkey';
const VARIANT = 'community';

// Splash: the same spinning-logo welcome as /quiz, played once per visitor. The decision is made
// once per page load (module flag) so StrictMode's double-effect neither strands nor skips it.
let splashDecided: boolean | null = null;
const SPLASH_MS = 7500;

/**
 * Which screen this visit opens on, decided during the FIRST RENDER. Deciding it in an effect
 * painted one frame of the splash at everyone who was meant to skip it — the logo starting to
 * spin and then vanishing, which reads as a broken start screen.
 */
const openingPhase = (): 'splash' | 'quiz' => {
    if (typeof window === 'undefined') return 'splash';
    if (splashDecided === null) {
        // Handed over from /quiz's audience question: it has already played the splash, so open on
        // question 1. A sessionStorage flag rather than a URL hash — AdSense's vignette rewrites
        // the hash to #google_vignette. #start is still honoured for a pasted link.
        const handedOver = sessionStorage.getItem('sp_cq_skip_splash') === '1' || window.location.hash === '#start';
        sessionStorage.removeItem('sp_cq_skip_splash');
        splashDecided = !handedOver && !localStorage.getItem('sp_cq_splash_seen');
    }

    return splashDecided ? 'splash' : 'quiz';
};

const uuid = (): string =>
    (crypto?.randomUUID?.() ?? 'k-' + Math.random().toString(36).slice(2) + Date.now().toString(36));

/**
 * One row per visitor, upserted as they go — the same endpoint and storage key the original quiz
 * uses, so a visitor who came through an ad is one person in the report either way.
 */
export function recordQuizResponse(variant: string, payload: Record<string, any> = {}, storageKey: string = R_KEY) {
    try {
        let key = localStorage.getItem(storageKey);
        if (!key) {
            key = uuid();
            localStorage.setItem(storageKey, key);
        }
        const token = localStorage.getItem('jwt_token');
        fetch(config.apiUrl + '/quiz/response', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(token ? {Authorization: 'Bearer ' + token} : {}),
            },
            body: JSON.stringify({key, variant, source_url: window.location.href, ...payload}),
            keepalive: true,
        }).catch(() => {});
    } catch { /* private mode: the quiz still works, we just can't report on it */ }
}

const recordResponse = (payload: Record<string, any> = {}) => recordQuizResponse(VARIANT, payload);

/** What a club might want help with. The value is what lands in the report and, later, their checklist. */
const FEATURES: { val: string; label: string; blurb: string; emoji: string }[] = [
    {val: 'registration', label: 'Registration & payments', blurb: 'Players sign themselves up and pay online', emoji: '💳'},
    {val: 'fixtures', label: 'Fixtures & scheduling', blurb: 'Draws and schedules built for you', emoji: '🗓️'},
    {val: 'live_scores', label: 'Live scores & standings', blurb: 'Tables that update themselves', emoji: '📊'},
    {val: 'live_updates', label: 'Live event updates', blurb: 'Everyone watching sees each result land', emoji: '⚡'},
    {val: 'players', label: 'Players & teams', blurb: 'One list of who plays for you', emoji: '👥'},
    {val: 'crm', label: 'Player / team CRM', blurb: 'Contact details, history and groups in one place', emoji: '🗂️'},
    {val: 'match_day', label: 'Match-day check-in', blurb: 'Who has arrived, who has paid, which court', emoji: '✅'},
    {val: 'engagement', label: 'Keeping players coming back', blurb: 'Leaderboards, streaks and club rewards', emoji: '🏅'},
    {val: 'comms', label: 'Messaging players', blurb: 'Reminders and results by WhatsApp and email', emoji: '💬'},
    {val: 'club_page', label: 'A public club page', blurb: 'Somewhere to send new members', emoji: '🌐'},
    {val: 'streaming', label: 'Streaming', blurb: 'A live score bug for OBS, Streamlabs and YouTube', emoji: '📺'},
    {val: 'notifications', label: 'Player notifications', blurb: 'Fixtures, results and reminders straight to their phone', emoji: '🔔'},
    {val: 'player_app', label: 'Player app', blurb: 'Live updates, history and stats for every player', emoji: '📱'},
    {val: 'events', label: 'Events', blurb: 'Tournaments, leagues, ladders and socials in one place', emoji: '🏆'},
];

/**
 * How many people they run events for. The first thing we ask, because it is the thing that most
 * changes the answer: a twelve-player match night and a ninety-player league want different help.
 * The value is what lands in the report, so the bands stay stable.
 */
const SIZES: { val: string; label: string; emoji: string }[] = [
    {val: '4_10', label: '4–10 participants', emoji: '🎾'},
    {val: '11_30', label: '11–30 participants', emoji: '👥'},
    {val: '31_75', label: '31–75 participants', emoji: '🏟️'},
    {val: '75_plus', label: '75+ participants', emoji: '🏆'},
];

// Emoji for a surface entry the registry has no definition for yet (table tennis is offered here
// before it is a sport the platform models; it is stored as 'other' — see storedSportKey).
const UNMODELLED_SPORT_EMOJI: Record<string, string> = {table_tennis: '🏓'};

// The sports this quiz offers, from the sport registry's 'quiz_community' surface. The quiz records
// entry.value ('pickleball', 'table_tennis'); the account stores storedSportKey(value).
// Read at call time, never at module load (see quiz.tsx).
export const quizSports = (): { val: string; label: string; emoji: string }[] => sportsForSurface('quiz_community').map((entry) => ({
    val:   entry.value,
    label: entry.label,
    emoji: sportDefinition(entry.key)?.emoji || UNMODELLED_SPORT_EMOJI[entry.key] || '🏅',
}));

/**
 * The showcase. Each panel belongs to a feature, so step 4 leads with what they actually picked
 * and shows the rest underneath — the pitch is theirs first, ours second.
 */
const SHOWCASE: Record<string, { title: string; body: string; image?: string; images?: string[] }> = {
    registration: {
        title: 'They register, you get paid',
        body: 'A public page for every event: players enter their own details and pay by card, and the money lands in your bank. No spreadsheet, no chasing.',
        image: shotRegister,
    },
    fixtures: {
        title: 'Fixtures without the jigsaw',
        body: 'Pick a format and ScorePal builds the draw — round robin, groups into a knockout, ladders, Americano and more. Change something and it redraws.',
        image: shotDraws,
    },
    live_scores: {
        title: 'Standings that keep themselves',
        body: 'Scores go in from any phone and the table, the bracket and the public page all update at once. Nobody asks you who is top.',
        image: shotDraws,
    },
    live_updates: {
        title: 'Everyone knows the moment it happens',
        body: 'A result goes in on court and the public page, the big screen and the players watching at home all change at once. No refreshing, no group-chat updates from you.',
        image: shotMatchCentre,
    },
    players: {
        title: 'One list of your players',
        body: 'Everyone who has ever played, with their history, groups and stats. Add them once and reuse them for every event you run.',
        image: shotClubPlayers,
    },
    crm: {
        title: 'Your membership book, kept for you',
        body: 'Contact details, groups and every match they have played, in one searchable list. It fills itself in as they play, so you are never rebuilding a spreadsheet.',
        image: shotCrm,
    },
    match_day: {
        title: 'Run the day from one screen',
        body: 'Check players in as they arrive, see every court at a glance, and know when you will finish. Late arrivals and no-shows are two taps.',
        image: shotCreateGame,
    },
    engagement: {
        title: 'Give them a reason to come back',
        body: 'Club leaderboards, streaks and coins turn casual players into regulars — and a monthly competition between clubs, if you want it.',
        image: shotLeaderboard,
    },
    comms: {
        title: 'Tell everyone at once',
        body: 'Fixture reminders, results and schedule changes by WhatsApp and email, sent to the people they affect.',
        images: [shotSchedule, shotNotify],
    },
    club_page: {
        title: 'A home for your club',
        body: 'A public page with your events, your results and your branding — the link you put in your bio.',
        image: shotClubPage,
    },
    streaming: {
        title: 'Your scores on the stream',
        body: 'Every court gets a Stream link. Paste it into OBS, Streamlabs or a phone app and a live score bug sits on your video, updating as the desk records points.',
        images: [shotOverlayFrame, shotStreamCourt],
    },
    notifications: {
        title: 'Players hear it first',
        body: 'Fixtures, results and schedule changes reach players by WhatsApp and email the moment they happen — no group chat to run.',
    },
    player_app: {
        title: 'An app for every player',
        body: 'Players who claim their profile get live updates on the day, plus their full match history and stats to keep them coming back.',
        image: shotPlayerDashboard,
    },
    events: {
        title: 'Every event, one place',
        body: 'Tournaments, leagues, ladders and socials all run from the same account, with the same players and the same public page.',
        image: shotDraws,
    },
    default: {
        title: 'Your match day, on one board',
        body: 'Courts, timings, check-in and results in one place, with a big-screen view for the venue.',
        image: shotMatchCentre,
    },
};

/** Questions with a progress bar. The showcase and signup screens follow, at a full bar. */
const QUESTIONS = 4;

function CommunityQuizPage() {
    const jwt = useJwtAuth();
    useSports(); // re-render with the full sport list once the live registry lands

    const [phase, setPhase] = useState<'splash' | 'quiz'>(openingPhase);
    const [step, setStep] = useState(1);
    const [size, setSize] = useState('');
    const [features, setFeatures] = useState<string[]>([]);
    const [otherFeature, setOtherFeature] = useState('');
    const [sport, setSport] = useState('');
    const [community, setCommunity] = useState('');
    // Which of the two ways in they took from "Here's what you'll be running". Kept in state, not
    // just sent once: every later record rewrites the whole answers object, and the signup record
    // that follows "Explore features" would otherwise wipe the choice it came from.
    const [nextStep, setNextStep] = useState<'' | 'explore' | 'setup_event'>('');
    // "Set up a new event now" first asks which kind, so the builder opens on the right one.
    const [choosingEvent, setChoosingEvent] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const top = useRef<HTMLDivElement | null>(null);

    useEffect(() => { top.current?.scrollIntoView({block: 'start'}); }, [step]);

    useEffect(() => {
        localStorage.setItem('sp_cq_splash_seen', '1');
        if (!splashDecided) {
            setPhase('quiz');
            return;
        }
        const t = setTimeout(() => {
            splashDecided = false; // played — in-session revisits skip it
            setPhase('quiz');
        }, SPLASH_MS);
        return () => clearTimeout(t);
    }, []);

    const answers = () => ({
        audience: 'community',
        ...(size ? {size} : {}),
        features: features.join(','),
        ...(otherFeature.trim() ? {features_other: otherFeature.trim().slice(0, 200)} : {}),
        ...(sport ? {sport} : {}),
        ...(community.trim() ? {community: community.trim().slice(0, 60)} : {}),
        ...(nextStep ? {next_step: nextStep} : {}),
    });

    /**
     * Hand the picks to the app. The account is only seconds old at this point, so there is nowhere
     * on the server for them yet — localStorage carries them across the page load and _app turns
     * them into the checklist popup on the first screen inside.
     */
    const stashChecklist = () => {
        const picked = features.filter((f) => itemFor(f));
        if (!picked.length && !otherFeature.trim() && !sport) return;
        writeChecklist({features: picked, other: otherFeature.trim().slice(0, 200) || undefined, sport: sport || undefined, done: []});
    };

    /**
     * The audience question that used to sit here now opens /quiz and routes people in, so anyone
     * on this page has already said they run a club. The first thing we ask them is how big it is.
     */
    const chooseSize = (val: string) => {
        setSize(val);
        recordResponse({answers: {...answers(), size: val}});
        setStep(2);
    };

    const toggleFeature = (val: string) =>
        setFeatures((prev) => (prev.includes(val) ? prev.filter((f) => f !== val) : [...prev, val]));

    const submitFeatures = () => {
        if (!features.length && !otherFeature.trim()) {
            setError('Pick at least one — it decides what we set up with you.');
            return;
        }
        setError('');
        recordResponse({answers: answers()});
        setStep(3);
    };

    const chooseSport = (val: string) => {
        setSport(val);
        recordResponse({answers: {...answers(), sport: val}});
        setStep(4);
    };

    const submitCommunity = () => {
        setError('');
        // Named or not, this is the last question — the answer row is complete either way.
        recordResponse({answers: answers(), completed: true});
        setStep(5);
    };

    const signup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || !password) {
            setError('Please fill in every field.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                quiz_key: localStorage.getItem(R_KEY) || undefined,
                platform: 'web',
                profile: {
                    email: email.trim(), password, name: name.trim(), lastname: '', inviter_uid: '',
                    // The community quiz has no tickbox of its own; enrolled by default, like the
                    // signup form's pre-ticked one.
                    leaderboard_enrolled: true,
                },
                questionnaire: {
                    // The account and the club store the platform's sport key, not the quiz's
                    // answer value. The recorded quiz answer (recordResponse) stays as picked.
                    sport: sport ? storedSportKey(sport) : null,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    club: {
                        // What they called it on step 4 — this is the name on their public page.
                        type: ClubType.Community, name: community.trim() || `${name.trim()}'s Community`,
                        country_code: '', city: '', city_coordinates: null, meta: {},
                        // This funnel IS the regular-events answer. Sent explicitly so the club
                        // counts as having answered and is never stopped mid-funnel to be asked.
                        organizer_path: 'community',
                    },
                },
            });
            if (res?.error) { setError(res.error); setBusy(false); return; }
            // Unconditionally: setFieldsFromResponse is the one that knows the token's field name,
            // and guessing it here is how this funnel used to sign people up and then land them on
            // the login page.
            jwt.setFieldsFromResponse(res);
            recordResponse({answers: answers(), completed: true}); // token is stored — links the row to the account
            stashChecklist();
            // The generic "create your first competition" popup would cover the checklist they were
            // just promised, so mark it seen the way /quiz does.
            sessionStorage.setItem('welcome_tutorial_seen', '"y"'); // useSessionStorage stores JSON
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            // The same conversion events /quiz and /signup/thank-you/ send — without these, every
            // signup this funnel wins is invisible in Ads.
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                setAdsUserData(email.trim()); // enhanced conversion matching — before the conversion event
                gtag('event', 'sign_up', {method: 'scorepal', signup_type: 'organizer'});
                // transaction_id = uid: Google's dedupe key, shared with /signup/thank-you/.
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_', transaction_id: res?.user?.uid || ''});
                if (res?.user?.uid) sessionStorage.setItem(`sp_signup_fired:${res.user.uid}`, '1');
            }
            // /profiles/me/ rather than /profiles/{uid}/: it resolves to whichever profile this
            // person actually has, which for everyone arriving through this funnel is their
            // community. Aiming straight at /profiles/{uid}/ lands a community organiser on their
            // PLAYER profile — leaderboard, ELO, "find a game" — which is not what they just
            // signed up to run. This is the same link the sidebar's "Community Profile" uses.
            window.location.assign('/profiles/me/');
        } catch (err: any) {
            setError(err?.message || 'Something went wrong. Please try again.');
            setBusy(false);
        }
    };

    // Straight to the event builder on /quiz, which needs only an event type and a sport: it
    // reads both from the same session stash its own questions write, and the #setup hash tells
    // it to skip the splash and the questions. Their picks still ride along as the checklist.
    // "Explore features with a free account": on to the signup step. Recorded here, at the click,
    // so someone who takes this way in and then leaves at signup is still counted as having chosen
    // it — that gap is exactly what the report is for.
    const exploreFeatures = () => {
        setNextStep('explore');
        recordResponse({answers: {...answers(), next_step: 'explore'}});
        setStep(6);
    };

    // The kind rides into the builder as its first answer — the same values /quiz asks for — so it
    // opens straight on "Set up your league" or "Set up your tournament".
    const setupEventNow = (kind: 'tournament' | 'league') => {
        setNextStep('setup_event');
        recordResponse({answers: {...answers(), next_step: 'setup_event', event_kind: kind}, completed: true});
        stashChecklist();
        const known = ['padel', 'pickleball', 'tennis', 'badminton', 'squash', 'football', 'basketball', 'volleyball', 'cricket', 'esports', 'roundnet'];
        const quizAnswers = known.includes(sport)
            ? {'1': kind, '2': sport}
            : {'1': kind, '2': 'other', '2_other': sport === 'table_tennis' ? 'Table tennis' : ''};
        try { sessionStorage.setItem('sp_quiz_answers', JSON.stringify(quizAnswers)); } catch { /* the builder asks instead */ }
        window.location.assign('/quiz/#setup');
    };

    const googleSignup = () => {
        recordResponse({answers: answers(), completed: true});
        stashChecklist();
        // No uid to aim at until Google hands them back, so /profiles/me/ resolves it — the
        // checklist rides in localStorage and opens wherever that lands them.
        startGoogleSignIn('/profiles/me/');
    };

    const chosen = FEATURES.filter((f) => features.includes(f.val));
    const showcase = [
        ...chosen.map((f) => ({key: f.val, ...SHOWCASE[f.val]})),
        ...(chosen.length < 3 ? [{key: 'default', ...SHOWCASE.default}] : []),
    ];
    const theirs = community.trim();

    // Questions fill the bar; the two screens after them sit at the end of it.
    const pct = Math.min(100, Math.round((Math.min(step, QUESTIONS) / QUESTIONS) * 100));
    const stepLabel = step <= QUESTIONS ? `Step ${step} of ${QUESTIONS}`
        : step === 5 ? 'Your features' : 'Create your account';

    return (
        <div className="sp-quiz" ref={top}>
            <title>Run your club on ScorePal — ScorePal</title>
            <meta name="robots" content="noindex" />
            <style>{QUIZ_FUNNEL_CSS}</style>

            <div className="wrap">
                {phase === 'splash' && (
                    <div className="sp-splash">
                        <div className="pill"><img src="/favicon-180x180.png" alt="ScorePal" width={118} height={118} /></div>
                        <p className="word">Welcome to ScorePal</p>
                        <p className="tag">The new way to run your club's events</p>
                        <div className="ltrack"><div className="lbar" /></div>
                        <p className="lmsg">Let's get started.</p>
                    </div>
                )}

                {phase === 'quiz' && <div className="qhead">
                    <div className="pmeta"><span>{stepLabel}</span><span>{pct}%</span></div>
                    <div className="ptrack"><div className="pbar" style={{width: `${pct}%`}} /></div>
                </div>}

                {phase === 'quiz' && <div className="qstep" key={step}>
                    {step === 1 && (
                        <>
                            <h1>How big is your community or club?</h1>
                            <p className="sub">It shapes what we set up with you — a match night and a 90-player league need different things.</p>
                            <div className="opts2">
                                {SIZES.map((s) => (
                                    <button type="button" key={s.val} className={`opt${size === s.val ? ' sel' : ''}`}
                                            onClick={() => chooseSize(s.val)}>
                                        <span className="oemoji">{s.emoji}</span>{s.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <h1>What would help you most?</h1>
                            <p className="sub">Pick everything that applies. We'll set these up with you after you join.</p>
                            <div className="opts2">
                                {FEATURES.map((f) => (
                                    <button type="button" key={f.val}
                                            className={`opt${features.includes(f.val) ? ' sel' : ''}`}
                                            aria-pressed={features.includes(f.val)}
                                            onClick={() => toggleFeature(f.val)}>
                                        <span className="oemoji">{f.emoji}</span>{f.label}
                                        <span className="odesc">{f.blurb}</span>
                                    </button>
                                ))}
                            </div>
                            <label htmlFor="cq-other">Something else?</label>
                            <input id="cq-other" className="tin" value={otherFeature} maxLength={200}
                                   onChange={(e) => setOtherFeature(e.target.value)}
                                   placeholder="Tell us what you need — we read every one" />
                            {error && <div className="err">{error}</div>}
                            <button type="button" className="opt osubmit" onClick={submitFeatures}>Continue →</button>
                            <button type="button" className="back" onClick={() => setStep(1)}>← Back</button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <h1>What sport do you manage?</h1>
                            <p className="sub">Scoring, formats and standings come set up for your sport.</p>
                            <div className="opts2">
                                {quizSports().map((s) => (
                                    <button type="button" key={s.val} className={`opt${sport === s.val ? ' sel' : ''}`}
                                            onClick={() => chooseSport(s.val)}>
                                        <span className="oemoji">{s.emoji}</span>{s.label}
                                    </button>
                                ))}
                            </div>
                            <button type="button" className="back" onClick={() => setStep(2)}>← Back</button>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            <h1>What's your community called?</h1>
                            <p className="sub">
                                This is the name on your public page and on every event you run. You can change it later.
                            </p>
                            <label htmlFor="cq-community">Community name</label>
                            <input id="cq-community" className="tin" value={community} maxLength={60}
                                   onChange={(e) => setCommunity(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === 'Enter') submitCommunity(); }}
                                   placeholder="Riverside Padel Club" autoFocus />
                            <button type="button" className="opt osubmit" onClick={submitCommunity}>Continue →</button>
                            {/* Not required: a name is easy to change later, and a blank box is no
                                reason to lose someone one question short of signing up. */}
                            <button type="button" className="back" onClick={submitCommunity}>I'll name it later</button>
                            <button type="button" className="back" style={{marginLeft: 18}} onClick={() => setStep(3)}>← Back</button>
                        </>
                    )}

                    {step === 5 && (
                        <>
                            <h1>{theirs ? `What ${theirs} will be running` : "Here's what you'll be running"}</h1>
                            <p className="sub">
                                {chosen.length
                                    ? 'Everything you picked, ready the day you join.'
                                    : 'What ScorePal does for a club that runs events all year.'}
                            </p>
                            {/* The two ways in sit above the showcase: nobody should have to scroll past
                                every panel to find them. */}
                            <button type="button" className="opt osubmit" onClick={exploreFeatures}>Explore features with a free account →</button>
                            {!choosingEvent ? (
                                <button type="button" className="opt oalt" onClick={() => setChoosingEvent(true)}>Set up a new event now →</button>
                            ) : (
                                <>
                                    <p className="sub" style={{marginBottom: 10}}>What are you setting up?</p>
                                    <div className="opts2">
                                        <button type="button" className="opt" onClick={() => setupEventNow('tournament')}>🏆 A tournament</button>
                                        <button type="button" className="opt" onClick={() => setupEventNow('league')}>📅 A league season</button>
                                    </div>
                                </>
                            )}
                            <p className="small">Free to start. No card needed. Setting up an event takes about two minutes.</p>
                            {showcase.map((panel) => (
                                <div className="panel" key={panel.key}>
                                    <h3>{panel.title}</h3>
                                    <p>{panel.body}</p>
                                    {panel.image && <img src={panel.image} alt="" loading="lazy" />}
                                    {panel.images?.map((src) => <img key={src} src={src} alt="" loading="lazy" />)}
                                </div>
                            ))}
                            <button type="button" className="back" onClick={() => setStep(4)}>← Back</button>
                        </>
                    )}

                    {step === 6 && (
                        <>
                            <h1>Create your account</h1>
                            <p className="sub">Your picks come with you — your profile opens on a checklist of them.</p>
                            <button type="button" className="opt ogoogle" onClick={googleSignup}>
                                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" /><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z" /></svg>
                                Continue with Google
                            </button>
                            <div className="or-div">or</div>
                            <form onSubmit={signup}>
                                <label htmlFor="cq-name">Your name</label>
                                <input id="cq-name" className="tin" value={name} onChange={(e) => setName(e.target.value)}
                                       autoComplete="name" />
                                <label htmlFor="cq-email">Email</label>
                                <input id="cq-email" className="tin" type="email" value={email}
                                       onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                                <label htmlFor="cq-password">Password</label>
                                <input id="cq-password" className="tin" type="password" value={password}
                                       onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                                {error && <div className="err">{error}</div>}
                                <button type="submit" className="opt osubmit" disabled={busy}>
                                    {busy ? 'Creating your account…' : 'Create my account →'}
                                </button>
                            </form>
                            <button type="button" className="back" onClick={() => setStep(5)}>← Back</button>
                            <p className="small">
                                By joining you agree to our <a href="/terms/" target="_blank" rel="noreferrer" style={{color: '#0B3DB0', textDecoration: 'underline'}}>Terms</a>.
                                Already with us? <a href="/login/" style={{color: '#0B3DB0', textDecoration: 'underline'}}>Log in</a>.
                            </p>
                        </>
                    )}
                </div>}
            </div>
        </div>
    );
}

/** The look of the organiser funnels (/quiz-community/, /scorepal-quiz/): white, black borders, ScorePal blue and yellow. */
export const QUIZ_FUNNEL_CSS = `
                .sp-quiz { min-height:100dvh; background:#fff;
                    font-family:Inter,sans-serif; color:#111; display:flex; flex-direction:column; align-items:center;
                    padding:20px 16px 48px; }
                .sp-quiz .wrap { width:100%; max-width:560px; }
                @keyframes spFadeUp { from {opacity:0; transform:translateY(12px)} to {opacity:1; transform:none} }
                @keyframes spLogoSpin { 0% {transform:rotateY(0) scale(.6); opacity:0} 60% {opacity:1}
                    100% {transform:rotateY(720deg) scale(1); opacity:1} }
                @keyframes spLoad { to { width:100% } }
                .sp-splash { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:70dvh; text-align:center; }
                .sp-splash .pill { width:180px; height:180px; border-radius:50%; background:#ffffff; display:grid; place-items:center;
                    border:3px solid #0B3DB0; box-shadow:0 10px 30px rgba(11,61,176,.18), 0 0 0 8px rgba(242,196,30,.35);
                    animation:spLogoSpin 2.8s cubic-bezier(.2,.7,.3,1) both; }
                .sp-splash img { width:118px; height:118px; }
                .sp-splash .word { font-family:'Bebas Neue',sans-serif; color:#0B3DB0; font-size:3.2rem; letter-spacing:.06em; margin-top:18px;
                    animation:spFadeUp .6s ease-out 1.6s both; }
                .sp-splash .tag { color:#444; font-size:1.05rem; margin-top:4px; animation:spFadeUp .6s ease-out 2.2s both; }
                .sp-splash .ltrack { width:min(260px,70%); height:6px; margin-top:26px; border-radius:99px;
                    background:#e8eefb; overflow:hidden; animation:spFadeUp .5s ease-out 2.6s both; }
                .sp-splash .lbar { height:100%; width:0; border-radius:99px; background:#F2C41E; animation:spLoad 4.6s ease-in-out 2.7s forwards; }
                .sp-splash .lmsg { color:#555; font-size:.85rem; margin-top:10px; animation:spFadeUp .5s ease-out 3s both; }
                .sp-quiz .opt.oalt { text-align:center; background:#fff; border-color:#0B3DB0; color:#0B3DB0; font-weight:800; }
                .sp-quiz .opt.oalt:hover { background:#f3f6fd; border-color:#0B3DB0; }

                .sp-quiz .qhead { margin:8px 0 18px; animation:spFadeUp .4s ease-out both; }
                .sp-quiz .pmeta { display:flex; justify-content:space-between; font-size:.8rem; color:#444; margin-bottom:6px; }
                .sp-quiz .ptrack { height:8px; border-radius:99px; background:#e8eefb; overflow:hidden; }
                .sp-quiz .pbar { height:100%; border-radius:99px; background:#F2C41E; transition:width .35s ease; }

                .sp-quiz h1 { font-family:'Bebas Neue',sans-serif; font-size:clamp(1.7rem,5.5vw,2.3rem); line-height:1.1;
                    letter-spacing:.02em; margin:6px 0 4px; color:#0B3DB0; }
                .sp-quiz .sub { color:#444; font-size:.95rem; margin:0 0 18px; }
                .sp-quiz .qstep { animation:spFadeUp .4s ease-out both; }
                .sp-quiz .opt { display:block; width:100%; text-align:left; background:#fff;
                    border:1.5px solid #111; color:#111; border-radius:14px; padding:15px 18px;
                    font-size:1rem; font-weight:600; margin-bottom:10px; cursor:pointer; transition:all .15s ease; }
                .sp-quiz .opt:hover, .sp-quiz .opt:active { background:#f3f6fd; border-color:#0B3DB0; transform:translateY(-1px); }
                .sp-quiz .opt.sel { background:#FFF8DB; border-color:#0B3DB0; box-shadow:inset 0 0 0 1.5px #0B3DB0; }
                .sp-quiz .opt .odesc { display:block; font-weight:400; font-size:.82rem; color:#555; margin-top:2px; }
                .sp-quiz .opt .oemoji { display:inline-block; width:1.7em; font-size:1.1em; }
                .sp-quiz .opts2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:10px; }
                .sp-quiz .opts2 .opt { margin-bottom:0; padding:13px 14px; }
                @media (max-width:400px) { .sp-quiz .opts2 { grid-template-columns:1fr; } }
                .sp-quiz .opt.osubmit { text-align:center; background:#F2C41E; color:#06246E; border-color:#F2C41E;
                    font-weight:800; font-size:1.05rem; margin-top:16px; box-shadow:0 5px 0 #c9a10f, 0 10px 24px rgba(242,196,30,.35); }
                .sp-quiz .opt.osubmit:hover { background:#ffd23f; }
                .sp-quiz .opt.osubmit:disabled { opacity:.45; cursor:default; transform:none; }
                /* Same pill as /quiz: white, centred, with the Google mark beside the label. */
                .sp-quiz .opt.ogoogle { background:#fff; color:#111; border-color:#111; font-weight:800;
                    display:flex; align-items:center; justify-content:center; gap:10px; }
                .sp-quiz .or-div { text-align:center; margin:12px 0; color:#555; font-size:12px;
                    text-transform:uppercase; letter-spacing:.14em; }

                .sp-quiz label { display:block; font-family:'Bebas Neue',sans-serif; font-size:1.15rem; letter-spacing:.05em;
                    color:#0B3DB0; margin:18px 0 7px; }
                .sp-quiz label:first-of-type { margin-top:4px; }
                .sp-quiz .tin { width:100%; background:#fff; border:1.5px solid #111;
                    border-radius:12px; color:#111; font-size:1rem; padding:12px 14px; margin-bottom:14px; outline:none; }
                .sp-quiz .tin::placeholder { color:#8a8a8a; }
                .sp-quiz .tin:focus { border-color:#0B3DB0; box-shadow:0 0 0 2px rgba(11,61,176,.25); }
                .sp-quiz .err { background:#fdecea; border:1px solid #c62835; color:#8a1c24;
                    font-size:.85rem; border-radius:10px; padding:8px 12px; margin-bottom:12px; }
                .sp-quiz .back { background:none; border:0; color:#0B3DB0; font-size:.85rem; cursor:pointer;
                    padding:0; margin-top:16px; text-decoration:underline; }
                .sp-quiz .small { color:#555; font-size:.8rem; margin-top:14px; line-height:1.5; }

                .sp-quiz .panel { background:#fff; color:#111; border:1.5px solid #111; border-radius:16px; padding:18px; margin-bottom:12px;
                    animation:spFadeUp .4s ease-out both; }
                .sp-quiz .panel h3 { font-size:1.1rem; margin:0 0 6px; color:#0B3DB0; }
                .sp-quiz .panel p { margin:0; color:#4A5B7C; font-size:.92rem; line-height:1.55; }
                .sp-quiz .panel img { width:100%; border-radius:11px; margin-top:13px; display:block; border:1px solid #e2e8f0; }
`;
